import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Lock, CreditCard, Star, Clock, Download, CheckCircle, ArrowDown, Sparkles } from 'lucide-react';
import { PriceDisplay } from './PriceDisplay';
import { trackAddToCart } from '../utils/metaPixel';
export const PricingOffer = () => {
  const includes = ['El Método Sueño Profundo (PDF)', 'Rutina de 72 Horas Cheat Sheet', 'Playlist de Ruido Rosa y Tonos', 'Video Mini-Curso de Implementación', 'Acceso de por vida y actualizaciones'];
  // Generate random particles for the background
  const particles = Array.from({
    length: 20
  }).map((_, i) => ({
    id: i,
    size: Math.random() * 4 + 2,
    x: Math.random() * 100,
    y: Math.random() * 100,
    duration: Math.random() * 20 + 10,
    delay: Math.random() * 5
  }));
  return <section id="pricing" className="py-24 px-6 relative overflow-hidden bg-brand-navy" data-id="element-108">
      {/* Dynamic Layered Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0" data-id="element-109">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-gold/15 via-brand-navy to-brand-navy" data-id="element-110"></div>

        {/* Floating Particles */}
        {particles.map(p => <motion.div key={p.id} className="absolute rounded-full bg-brand-gold/40 shadow-[0_0_10px_rgba(212,175,55,0.8)]" style={{
        width: p.size,
        height: p.size,
        left: `${p.x}%`,
        top: `${p.y}%`
      }} animate={{
        y: [0, -100, 0],
        opacity: [0.2, 0.8, 0.2],
        scale: [1, 1.5, 1]
      }} transition={{
        duration: p.duration,
        repeat: Infinity,
        delay: p.delay,
        ease: 'linear'
      }} data-id="element-111" />)}
      </div>

      <div className="max-w-4xl mx-auto relative z-10" data-id="element-112">
        {/* Proof / Believability */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-12" data-id="element-113">
          <div className="flex justify-center gap-1 text-brand-gold mb-6" data-id="element-114">
            {[...Array(5)].map((_, i) => <Star key={i} className="w-6 h-6 fill-current drop-shadow-md" data-id="element-115" />)}
          </div>
          <h2 className="text-3xl md:text-5xl font-bold mb-6 text-white drop-shadow-lg" data-id="element-116">
            Lo que cambia en 7 días:
          </h2>
          <p className="text-xl md:text-2xl text-white/90 leading-relaxed font-playfair italic max-w-3xl mx-auto" data-id="element-117">
            "Pasarás de una madre agotada que llora de frustración a las 2 AM, a
            una mujer con claridad mental que puede disfrutar de su bebé por la
            mañana, sabiendo que ambos han descansado."
          </p>
        </motion.div>

        {/* The Premium Offer Box */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 30
      }} whileInView={{
        opacity: 1,
        scale: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        type: 'spring',
        bounce: 0.4
      }} className="bg-gradient-to-b from-brand-cream to-white text-brand-charcoal rounded-3xl p-6 md:p-10 border-2 border-brand-gold shadow-[0_0_40px_rgba(212,175,55,0.3),0_20px_50px_rgba(0,0,0,0.5)] max-w-3xl mx-auto relative" data-id="element-118">
          {/* Pulsing Urgency Badge */}
          <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 bg-red-600 text-white font-black px-6 md:px-8 py-2 rounded-full text-sm md:text-base shadow-[0_0_20px_rgba(220,38,38,0.6)] whitespace-nowrap animate-pulse flex items-center gap-2 border border-red-400 z-20" data-id="element-119">
            <Clock className="w-5 h-5" data-id="element-120" /> OFERTA EXPIRA PRONTO
          </div>

          <div className="text-center mb-8 mt-4" data-id="element-121">
            <h3 className="text-3xl md:text-4xl font-black text-brand-navy mb-2 uppercase tracking-tight" data-id="element-122">
              Recupera Tu Vida <span className="text-brand-gold" data-id="element-123">Hoy</span>
            </h3>
            <p className="text-lg text-brand-charcoal/80 font-medium" data-id="element-124">
              El agotamiento no tiene por qué ser tu normalidad.
            </p>
          </div>

          {/* Content Grid: Image + Checklist */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center mb-8" data-id="element-125">
            <motion.div whileHover={{
            scale: 1.02
          }} transition={{
            type: 'spring',
            stiffness: 300
          }} className="relative" data-id="element-126">
              <div className="absolute inset-0 bg-brand-gold/20 blur-2xl rounded-full" data-id="element-127"></div>
              <img src="https://cdn.magicpatterns.com/uploads/q3HLZwTLMHCKnnHaks8gaD/LP2.png" alt="El Método Sueño Profundo - Paquete completo" className="w-full max-w-sm mx-auto rounded-2xl relative z-10 drop-shadow-2xl" data-id="element-128" />
            </motion.div>

            <div className="space-y-3" data-id="element-129">
              <h4 className="font-bold text-brand-navy text-lg mb-4 border-b border-brand-lavender/30 pb-2" data-id="element-130">
                Tu paquete incluye:
              </h4>
              {includes.map((item, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.3 + i * 0.1
            }} className="flex items-start gap-3" data-id="element-131">
                  <CheckCircle className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-132" />
                  <span className="text-brand-charcoal/90 font-medium leading-tight" data-id="element-133">
                    {item}
                  </span>
                </motion.div>)}
            </div>
          </div>

          {/* Price Display Block */}
          <div className="bg-[#1A2640] rounded-2xl p-6 md:p-8 mb-8 relative overflow-hidden text-center border border-brand-gold/40 shadow-inner" data-id="element-134">
            <div className="absolute top-0 right-0 w-32 h-32 bg-brand-gold/10 rounded-full blur-2xl" data-id="element-135"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-brand-lavender/10 rounded-full blur-2xl" data-id="element-136"></div>

            <div className="relative z-10" data-id="element-137">
              <div className="flex items-center justify-center gap-3 mb-2" data-id="element-138">
                <span className="text-2xl text-red-400 line-through font-bold decoration-red-500/80" data-id="element-139">
                  $67 USD
                </span>
                <span className="bg-red-600 text-white text-xs md:text-sm font-bold px-3 py-1 rounded-full uppercase tracking-wide shadow-md" data-id="element-140">
                  -91% OFF
                </span>
              </div>

              <div className="text-6xl md:text-8xl font-black mb-3 tracking-tighter" style={{
              background: 'linear-gradient(170deg, #F5D060 0%, #D4AF37 40%, #B8962E 70%, #D4AF37 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              filter: 'drop-shadow(0 4px 0 rgba(154,123,31,0.6)) drop-shadow(0 8px 12px rgba(0,0,0,0.4))'
            }} data-id="element-141">
                <PriceDisplay data-id="element-142" />
              </div>

              <p className="text-brand-gold/90 text-sm md:text-base font-bold flex items-center justify-center gap-1.5 uppercase tracking-widest" data-id="element-143">
                <Sparkles className="w-4 h-4" data-id="element-144" /> Pago único. Acceso de por vida.
              </p>
            </div>
          </div>

          {/* CTA Area */}
          <div className="flex flex-col items-center relative z-10" data-id="element-145">
            <motion.div animate={{
            y: [0, 8, 0]
          }} transition={{
            repeat: Infinity,
            duration: 1.5,
            ease: 'easeInOut'
          }} className="mb-3" data-id="element-146">
              <ArrowDown className="text-brand-gold w-8 h-8 drop-shadow-md" data-id="element-147" />
            </motion.div>

            <a href="https://www.rutinaspracticas.com/cart/52836173414722:1" onClick={trackAddToCart} className="btn-gold-3d block w-full text-center text-white font-black text-2xl md:text-3xl py-5 md:py-6 px-8 rounded-xl transition-all transform hover:-translate-y-1 active:translate-y-0 mb-4 tracking-wide uppercase shadow-[0_10px_40px_rgba(212,175,55,0.4)]" data-id="element-148">
              ¡SÍ, QUIERO DORMIR OTRA VEZ!
            </a>

            <p className="text-sm md:text-base text-brand-charcoal/70 font-bold flex items-center gap-2 bg-brand-lavender/10 px-4 py-2 rounded-full" data-id="element-149">
              <Download className="w-4 h-4 text-brand-navy" data-id="element-150" />
              Descarga instantánea (Incluso a las 3 AM)
            </p>
          </div>

          {/* Trust Elements */}
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8 pt-8 mt-8 border-t border-brand-lavender/20" data-id="element-151">
            <div className="flex items-center gap-2 text-sm font-bold text-brand-navy" data-id="element-152">
              <div className="bg-brand-gold/20 p-2 rounded-full" data-id="element-153">
                <Lock className="w-4 h-4 text-brand-gold" data-id="element-154" />
              </div>
              Pago 100% Seguro
            </div>
            <div className="flex items-center gap-2 text-sm font-bold text-brand-navy" data-id="element-155">
              <div className="bg-brand-gold/20 p-2 rounded-full" data-id="element-156">
                <ShieldCheck className="w-4 h-4 text-brand-gold" data-id="element-157" />
              </div>
              Garantía de 7 Días
            </div>
            <div className="flex items-center gap-2 text-sm font-bold text-brand-navy" data-id="element-158">
              <div className="bg-brand-gold/20 p-2 rounded-full" data-id="element-159">
                <CreditCard className="w-4 h-4 text-brand-gold" data-id="element-160" />
              </div>
              Tarjetas / PayPal
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
};